import React, { useState, useMemo } from "react";
import { Input, Segmented } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { videoCategories } from "../../data/videoTutorials";
import { buildDashaiWorkflowCategories } from "../../data/dashaiWorkflowVideos";
import { foldSearch } from "../../utils/searchFold";
import { CategorySection, VideoPlayerModal } from "../VideoTutorialPage";
import { useWorkflowVisibility } from "@dashai/hooks/useWorkflowVisibility";

const SECTIONS = {
  DASHBOARDS: "dashboards",
  WORKFLOWS: "workflows",
};

// Video Tutorials tab body for LearnHubPage. Renders search + video grid +
// modal but no outer page layout or H1, which the hub owns.
//
// A segmented control switches between dashboard videos and DashAI workflow
// videos; the hub owns the selected `section`. The workflow segment honours
// workspace-level visibility via `useWorkflowVisibility` so workspaces only
// see workflows they have access to.
const TutorialsContent = ({ section: activeSection, onSectionChange }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedWorkspace = useSelector(
    (state) => state.auth?.selectedWorkspace
  );
  const selectedClient = selectedWorkspace?.key || null;
  const { visibleWorkflowIds } = useWorkflowVisibility(selectedClient);

  const dashaiCategories = useMemo(
    () => buildDashaiWorkflowCategories(visibleWorkflowIds),
    [visibleWorkflowIds]
  );

  const sourceCategories =
    activeSection === SECTIONS.WORKFLOWS ? dashaiCategories : videoCategories;

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  };

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return sourceCategories;
    }

    const query = foldSearch(searchQuery);
    return sourceCategories
      .map((category) => ({
        ...category,
        videos: category.videos.filter(
          (video) =>
            foldSearch(video.title).includes(query) ||
            foldSearch(video.description).includes(query) ||
            foldSearch(video.category).includes(query) ||
            foldSearch(video.sectionName).includes(query)
        ),
      }))
      .filter((category) => category.videos.length > 0);
  }, [searchQuery, sourceCategories]);

  const getCategoryStartIndex = (categoryIndex) => {
    let startIndex = 0;
    for (let i = 0; i < categoryIndex; i++) {
      startIndex += filteredCategories[i]?.videos.length || 0;
    }
    return startIndex;
  };

  const isWorkflowsEmpty =
    activeSection === SECTIONS.WORKFLOWS && dashaiCategories.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="self-start">
        <Segmented
          className="learn-hub-segmented"
          options={[
            { label: "Dashboards", value: SECTIONS.DASHBOARDS },
            { label: "DashAI Workflows", value: SECTIONS.WORKFLOWS },
          ]}
          value={activeSection}
          onChange={onSectionChange}
          size="large"
        />
      </div>

      <div className="w-full">
        <Input
          type="text"
          placeholder={
            activeSection === SECTIONS.WORKFLOWS
              ? "Search DashAI workflow videos"
              : "Search video guides"
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,0.45)", marginRight: 8 }}
            />
          }
          style={{
            borderRadius: "999px",
            border: "1px solid rgba(0,0,0,0.15)",
            padding: "8px 30px 8px 22px",
            fontSize: "16px",
          }}
        />
      </div>

      <div className="flex flex-col gap-9">
        {filteredCategories.length > 0 ? (
          filteredCategories.map((category, categoryIndex) => (
            <CategorySection
              key={category.id}
              category={category}
              videos={category.videos}
              startIndex={getCategoryStartIndex(categoryIndex)}
              onVideoClick={handleVideoClick}
            />
          ))
        ) : isWorkflowsEmpty ? (
          <div className="text-center py-16">
            <p className="text-[rgba(0,0,0,0.45)] text-base">
              No DashAI workflow videos available for this workspace yet.
            </p>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-[rgba(0,0,0,0.45)] text-base">
              No videos found matching your search.
            </p>
            <button
              onClick={() => setSearchQuery("")}
              className="mt-4 text-[#0E49B5] hover:underline font-medium"
            >
              Clear search
            </button>
          </div>
        )}
      </div>

      <VideoPlayerModal
        open={isModalOpen}
        onClose={handleCloseModal}
        video={selectedVideo}
      />
    </div>
  );
};

export default TutorialsContent;
